import React, { FC, useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Grid, makeStyles, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@material-ui/core';
import { Page, PaperCustom, Breadcrumb, SearchInput } from 'components';
import NumberFormat from 'components/NumberFormat';
import PaginationCustom from 'components/PaginationCustom';
import useDebounce from 'hooks/useDebounce';
import { PURCHASE_PRICE_HISTORY_SUMMARY_BASE_URL } from 'constants/url';

type PurchasePriceHistorySummary = {
  id: number;
  productId: number;
  productName: string;
  supplierName: string;
  totalItem: number;
  typeUnit: string;
  lastPrice: number;
  hppWithoutShipping: number;
  orderDate?: string;
};

const useStyles = makeStyles({
  tableHead: {
    background: '#F6F7FB'
  },
  tableCellHead: {
    fontWeight: 600
  }
});

const PurchasePriceHistoryPage: FC = () => {
  const classes = useStyles();
  const [items, setItems] = useState<PurchasePriceHistorySummary[]>([]);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [count, setCount] = useState<number>(0);
  const [query, setQuery] = useState<string>('');

  const queryDebounce = useDebounce(query, 400);

  const fetchData = async () => {
    setIsLoadingData(true);
    const params = new URLSearchParams();

    if (queryDebounce) {
      params.append('keyword', queryDebounce);
    }
    params.append('page', currentPage.toString());

    try {
      const { data } = await axios.get(`${PURCHASE_PRICE_HISTORY_SUMMARY_BASE_URL}?${params.toString()}`);
      setItems(data.data);
      setCount(data.meta.last_page);
      setCurrentPage(data.meta.current_page);
    } catch (error) {
      console.log('error: ', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [query]);

  useEffect(() => {
    fetchData();
  }, [queryDebounce, currentPage]);

  return (
    <Page title='History Harga Pembelian Barang'>
      <Container>
        <Grid container direction='row'>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Typography variant='h1'>History Harga Pembelian Barang</Typography>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Breadcrumb />
          </Grid>
        </Grid>
        <PaperCustom>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <SearchInput
                withBorder
                withTransition={false}
                width={180}
                placeHolder='Cari item atau supplier'
                iconColor='#989898'
                tableSearchValue={query}
                setTableSearchValue={setQuery}
              />
            </Grid>
            <Grid item xs={12}>
              <TableContainer component={Paper} variant='outlined'>
                <Table>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableCellHead}>Item</TableCell>
                      <TableCell className={classes.tableCellHead}>Supplier</TableCell>
                      <TableCell className={classes.tableCellHead} align='right'>
                        Qty &amp; Harga Beli Terakhir
                      </TableCell>
                      <TableCell className={classes.tableCellHead} align='right'>
                        HPP Tanpa Ongkir
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoadingData ? (
                      <TableRow>
                        <TableCell colSpan={4} align='center'>
                          <Typography>Memuat data...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : items.length > 0 ? (
                      items.map(item => (
                        <TableRow key={item.id}>
                          <TableCell>{item.productName}</TableCell>
                          <TableCell>{item.supplierName}</TableCell>
                          <TableCell align='right'>
                            <Typography component='div'>
                              {item.totalItem} {item.typeUnit}
                            </Typography>
                            <Typography component='div'>
                              <NumberFormat value={item.lastPrice} prefix={'Rp'} thousandSeparator={true} displayType='text' />
                            </Typography>
                          </TableCell>
                          <TableCell align='right'>
                            <NumberFormat value={item.hppWithoutShipping} prefix={'Rp'} thousandSeparator={true} displayType='text' />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align='center'>
                          <Typography>Data tidak ada.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid container justify='flex-end' item xl={12} md={12} sm={12}>
              <PaginationCustom
                marginTop='-.4em '
                show={true}
                sxPagination={{
                  count,
                  boundaryCount: 2,
                  variant: 'outlined',
                  shape: 'rounded',
                  onChange: (event, page) => {
                    setCurrentPage(page);
                  },
                  page: currentPage
                }}
                sxPopover={{
                  anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right'
                  },
                  transformOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right'
                  }
                }}
                customPageProps={{
                  defaultValue: currentPage,
                  maxValue: count,
                  onSubmit(value) {
                    setCurrentPage(value);
                  }
                }}
              />
            </Grid>
          </Grid>
        </PaperCustom>
      </Container>
    </Page>
  );
};

export default PurchasePriceHistoryPage;
